import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { listsApi } from '../../api/lists-api';
import type { IListCell, IListRow } from '../../api/lists-contracts';
import { refusalMessage } from '../../refusal';

/**
 * *Mark as exception…* (SR-525, C2 and D5): a Quality rating reason — unresponsive, not an FA,
 * rude — chosen from the survey's own options and written into Quality rating instead of a score.
 * survey-api turns any reason into Travel NO in the same save, so the advisor leaves the DD list;
 * nothing here decides that. One dialog, opened from the DD block's "⋯" menu and from the DD call
 * sheet's *Couldn't complete*; the write is the host's (`DdExceptionWrite`), as each holds the
 * field another way — the queue's Lists row at the version it was read with, the record page's
 * staff field on the newest live response.
 */

export interface IDdReason {
  code: string;
  label: string;
}

/** Writes the reason; null when it landed, else the sentence saying why not. */
export type DdExceptionWrite = (reason: IDdReason) => Promise<Nullable<string>>;

/** What a write over a value moved since it was read says — nothing overwritten. */
export const EXCEPTION_CONFLICT = 'Changed by someone else since you opened it — refresh and try again.';

/** What the block says once a reason landed. */
export function exceptionSaid(label: string, name: string): string {
  return `Marked as an exception: ${label}. Travel is now NO, so ${name} leaves the DD list.`;
}

/**
 * The queue's write: the Lists cell write of Quality rating at the version the row was read with,
 * as the record page's field would. Said as saved only where survey-api names the cell saved (or
 * already holding it); `onLanded` takes the cell as it landed.
 */
export function listCellException(
  row: IListRow,
  viewId: string,
  quality: string,
  onLanded: (cell: IListCell) => void
): DdExceptionWrite {
  return async (reason) => {
    try {
      const answer = await listsApi.edit({
        viewId,
        note: 'DD exception, from DD calls',
        cells: [
          {
            participantId: row.participantId,
            survey: row.survey,
            field: quality,
            value: { t: 'select', v: reason.code },
            version: row.cells[quality]?.version ?? 0
          }
        ]
      });
      const landed = [...answer.applied, ...answer.unchanged].find((cell) => cell.field === quality);
      if (!landed) {
        return answer.conflicts.length > 0
          ? EXCEPTION_CONFLICT
          : (answer.refused ?? []).length > 0
            ? `The system did not save it (${answer.refused![0].code}). Refresh and try again.`
            : 'The system did not answer for this cell, so nothing is known to be saved. Refresh and look.';
      }
      onLanded({ v: reason.code, version: landed.version, label: reason.label });
      return null;
    } catch (error) {
      return refusalMessage(error, 'The exception could not be saved.');
    }
  };
}

/** The record page's write: the staff field on the response the part's cycle is, quoting the
 *  version the page read (`If-Match`, 0 for an empty cell), then the page read again. A stale
 *  version is survey-api's `cell-conflict`, said as such and never written over. */
export function staffFieldException(
  responseId: string,
  quality: string,
  version: number,
  reread: () => void
): DdExceptionWrite {
  return async (reason) => {
    try {
      await internalFieldsApi.set(responseId, quality, { t: 'select', v: reason.code }, version);
      reread();
      return null;
    } catch (error) {
      if (error instanceof ApiError && error.isProblem(ProblemType.cellConflict)) {
        reread();
        return EXCEPTION_CONFLICT;
      }
      return refusalMessage(error, 'The exception could not be saved.');
    }
  };
}

export function ExceptionDialog({
  name,
  reasons,
  write,
  onClose,
  onMarked
}: {
  name: string;
  /** The survey's Quality rating reasons (`reasonOptions`). */
  reasons: readonly IDdReason[];
  write: DdExceptionWrite;
  onClose: () => void;
  onMarked: (sentence: string) => void;
}) {
  const formId = React.useId();
  const reasonId = React.useId();
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const chosen = reasons.find((one) => one.code === reason);
    if (!chosen) return;
    setBusy(true);
    setRefused(null);
    const refusal = await write(chosen);
    setBusy(false);
    if (refusal) {
      setRefused(refusal);
      return;
    }
    onMarked(exceptionSaid(chosen.label, name));
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent data-dd-exception="">
        <DialogHeader>
          <DialogTitle>Mark {name} as an exception</DialogTitle>
          <DialogDescription>
            The reason is written into Quality rating instead of a score. Travel becomes NO and the advisor leaves the DD
            list; a reason the Reinstatement worklist reviews brings them back there later.
          </DialogDescription>
        </DialogHeader>
        <DialogBody as="form" id={formId} onSubmit={(event) => void submit(event)}>
          <div className="grid gap-1 text-[0.8125rem]">
            <span id={reasonId}>Reason</span>
            <ChoiceSelect
              labelledBy={reasonId}
              value={reason}
              onChange={setReason}
              choices={[{ value: '', label: 'Choose a reason' }, ...reasons.map((one) => ({ value: one.code, label: one.label }))]}
              disabled={busy}
            />
          </div>
          {refused && (
            <p role="alert" className="text-[0.8125rem]">
              {refused}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button key="cancel" type="button" variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button key="mark" type="submit" form={formId} disabled={busy || reason === ''}>
            {busy ? 'Saving…' : 'Mark as exception'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
